import { PropertySet } from "datasheets/model/core/Workbook";
import { useAtom } from "jotai";
import { Button, MenuItem, Select, Stack, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Cancel } from "@mui/icons-material";
import { BaseDialog } from "../common/BaseDialog";
import { treeFilterAtom } from "./TreeAtoms";
import {
  Filter,
  PredicateName,
  Predicates,
  SearchFilter,
} from "./SearchFilter";
import { ColumnSelector } from "datasheets/components/selectors/ColumnSelector";

export interface ModalFormProps {
  modalIsOpen: boolean;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export function FilterDialog({
  modalIsOpen,
  setModalIsOpen,
  propertySet,
}: ModalFormProps & { propertySet: PropertySet }) {
  const [filters, setFilters] = useAtom(treeFilterAtom);
  const filterDisplay = (
    <>
      {[...filters, ...[undefined]].map((filter, index) => {
        return (
          <div>
            <FilterSelector
              key={index}
              propertySet={propertySet}
              addFilter={(
                column: string,
                predicate: PredicateName,
                value: string,
                not: boolean
              ) => {
                const filter: SearchFilter = {
                  property: column,
                  not: not,
                  filter: {
                    predicate: predicate,
                    operands: value,
                  },
                };
                !!filter
                  ? setFilters((filters) => {
                      const newFilters = [...filters];
                      newFilters[index] = filter;
                      return newFilters;
                    })
                  : setFilters((filters) => [...filters, filter]);
              }}
              filter={filter}
              removeFilter={() => {
                if (filter) {
                  setFilters((filters) => {
                    const newFilters = [...filters];
                    newFilters.splice(index, 1);
                    return newFilters;
                  });
                }
              }}
            />
          </div>
        );
      })}
    </>
  );
  return (
    <BaseDialog
      modalIsOpen={modalIsOpen}
      setModalIsOpen={setModalIsOpen}
      dialogTitle="Tree Filters"
      resetFormValues={() => {}}
      onSubmit={() => {}}
    >
      <Stack spacing={2}>
        <div>Filters: {filterDisplay}</div>
      </Stack>
    </BaseDialog>
  );
}
function FilterSelector({
  propertySet,
  filter,
  addFilter,
  removeFilter,
}: {
  propertySet: PropertySet;
  filter?: SearchFilter;
  addFilter: (
    column: string,
    predicate: PredicateName,
    value: string,
    not: boolean
  ) => void;
  removeFilter: () => void;
}) {
  const isDirty = useRef(false);
  const [initialFilter, setInitialFilter] = useState(filter);
  const [column, setColumn] = useState<string | undefined>(filter?.property);
  const [predicate, setPredicate] = useState<PredicateName>(
    filter?.filter.predicate ?? Predicates.EQUALS
  );
  const [value, setValue] = useState(filter?.filter.operands ?? "");
  const [not, setNot] = useState(filter?.not ?? false);
  useEffect(() => {
    if (column && value && isDirty.current) {
      isDirty.current = false;
      addFilter(column, predicate, value as string, not);
    }
    if (filter !== initialFilter) {
      if (
        filter &&
        (filter.property !== column ||
          filter.filter.predicate !== predicate ||
          filter.filter.operands !== value ||
          filter.not !== not)
      ) {
        setColumn(filter.property);
        setPredicate(filter.filter.predicate);
        setValue(filter.filter.operands);
        setNot(filter.not ?? false);
      } else if (!filter) {
        setColumn(undefined);
        setPredicate(Predicates.EQUALS);
        setValue("");
        setNot(false);
      }
      setInitialFilter(filter);
    }
  }, [addFilter, column, predicate, value, not]);

  return (
    <Stack direction="row">
      <Button
        onClick={() => {
          setNot((not) => !not);
          isDirty.current = true;
        }}
        sx={{ color: not ? "black" : "#D3D3D3" }}
      >
        !
      </Button>
      <ColumnSelector
        showTitle={false}
        title="Property"
        propertySet={propertySet}
        selection={column}
        onChange={(value) => {
          setColumn(value);
          isDirty.current = true;
        }}
        isSingleSelection={true}
      />
      <TextField
        select
        value={predicate}
        onChange={(event) => {
          setPredicate(event.target.value as PredicateName);
          isDirty.current = true;
        }}
        size="small"
      >
        {/* {Object.keys(Predicates).map((key) => (
              <MenuItem value={key}>{key}</MenuItem>
            ))} */}
        <MenuItem value={Predicates.EQUALS}>EQUALS</MenuItem>
        <MenuItem value={Predicates.CONTAINS}>CONTAINS</MenuItem>
      </TextField>
      <TextField
        hiddenLabel
        size="small"
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          isDirty.current = true;
        }}
      />
      {!!filter ? <Cancel onClick={removeFilter} /> : <></>}
    </Stack>
  );
}

export function FilterDisplay({
  columnNames,
}: {
  columnNames: { [key: string]: string };
}) {
  const [filters, setFilters] = useAtom(treeFilterAtom);
  return (
    <Stack direction="row">
      {filters.length > 0 ? "Current Filter: " : ""}
      {filters.map((filter) => (
        <>
          &nbsp;
          <FilterEntry filter={filter} columnNames={columnNames} />;
        </>
      ))}
    </Stack>
  );
}

function filterToString(filter: Filter): string {
  const operands = filter.operands.toString();
  return filter.predicate + " " + operands;
}
export function FilterEntry({
  filter,
  columnNames,
}: {
  filter: SearchFilter;
  columnNames: { [key: string]: string };
}) {
  return (
    <span>
      {filter.not ? "not" : ""} {columnNames[filter.property]}{" "}
      {filterToString(filter.filter)}
    </span>
  );
}
